/**
 * Completion Step Component
 *
 * Final step of the wizard - shows summary and start button.
 */

import { useTranslation } from '@/hooks/useTranslation';

interface CompletionStepProps {
  onComplete: () => void;
  onBack: () => void;
}

export function CompletionStep({ onComplete, onBack }: CompletionStepProps) {
  const { t } = useTranslation();

  return (
    <div className="text-center space-y-8">
      {/* Success icon */}
      <div className="flex justify-center">
        <div className="w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
          <svg
            className="w-14 h-14 text-green-600 dark:text-green-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>

      {/* Title */}
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 dark:text-secondary-100 mb-3">
          {t('wizard.completion.title', "You're All Set!")}
        </h2>
        <p className="text-lg text-secondary-600 dark:text-secondary-400 max-w-xl mx-auto">
          {t(
            'wizard.completion.subtitle',
            'Your Asgard Studio is configured and ready to create amazing videos.'
          )}
        </p>
      </div>

      {/* Tips */}
      <div className="bg-secondary-50 dark:bg-secondary-800/50 border border-secondary-200 dark:border-secondary-700 rounded-xl p-6 max-w-xl mx-auto text-left">
        <h3 className="font-semibold text-secondary-900 dark:text-secondary-100 mb-4">
          {t('wizard.completion.quickTips', 'Quick Tips to Get Started')}
        </h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="text-primary-500 font-bold">1.</span>
            <span className="text-secondary-700 dark:text-secondary-300">
              {t(
                'wizard.completion.tip1',
                'Start with the Video page to create your first AI-powered video'
              )}
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary-500 font-bold">2.</span>
            <span className="text-secondary-700 dark:text-secondary-300">
              {t('wizard.completion.tip2', 'Use the Image Generation tab to create custom visuals')}
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary-500 font-bold">3.</span>
            <span className="text-secondary-700 dark:text-secondary-300">
              {t(
                'wizard.completion.tip3',
                'Access Settings anytime to configure additional components'
              )}
            </span>
          </li>
        </ul>
      </div>

      {/* Settings reminder */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 max-w-xl mx-auto">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          {t(
            'wizard.completion.settingsReminder',
            'You can change component settings anytime from the sidebar menu.'
          )}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
        <button
          onClick={onComplete}
          className="w-full sm:w-auto px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors min-h-[44px]"
        >
          {t('wizard.completion.startCreating', 'Start Creating!')}
        </button>
        <button onClick={onBack} className="icon-button-muted">
          {t('wizard.completion.goBack', 'Go back and change settings')}
        </button>
      </div>
    </div>
  );
}

export default CompletionStep;
